import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, MessageCircle, X } from "lucide-react";
import PerguntaAgenda from "@/components/PerguntaAgenda";
import ChatRapido from "@/components/ChatRapido";
import { useGaveta, chaveContexto } from "@/context/GavetaContext";
import { useMensagens } from "@/context/MensagensContext";

// Lote 152 — gaveta lateral com duas abas independentes:
//  • Assistente IA — consulta à agenda: perguntas dos exemplos por regras (grátis); perguntas livres e resumo de processo
//    pela IA (Lote 153), somente leitura, com nomes trocados por códigos antes de sair do servidor;
//  • Equipe — chat interno. O contador de mensagens pertence SÓ a esta aba.
// Trocar de aba nunca envia nem compartilha nada: cada aba tem seu componente, seu histórico e seu rascunho
// (as duas ficam montadas, só uma visível), e o Assistente não tem nenhuma função de envio de mensagem.
export const EXEMPLOS_ASSISTENTE = [
  "O que exige minha atenção hoje?",
  "Quais publicações ainda precisam de agendamento?",
  "Tenho conflitos de horário nesta semana?",
  "Minha próxima audiência",
];

export const EXEMPLOS_PROCESSO = [
  "Resumir as últimas movimentações",
  "Existe algum prazo ou providência pendente?",
  "O que aconteceu nos últimos 30 dias?",
];

export const EXEMPLOS_AUDIENCIA = [
  "Resumir este compromisso",
  "O que preciso preparar para ele?",
  "Há andamento ou prazo relacionado?",
];

// Lote 154 — nome da tela para o Assistente entender "aqui" (lista fechada, igual à do servidor).
export function nomeDaTela(pathname) {
  const p = pathname || "";
  if (/^\/processos\/[^/]+/.test(p)) return "Processo";
  if (/^\/audiencia\/[^/]+/.test(p)) return "Audiência";
  const mapa = [["/dashboard", "Painel"], ["/audiencias", "Agenda"], ["/publicacoes", "Publicações"], ["/calendario", "Calendário"],
    ["/calculadora-prazos", "Calculadora de prazos"], ["/revisao", "Para validar"], ["/processos", "Processos"], ["/clientes", "Clientes"],
    ["/compartilhamentos", "Compartilhamentos"], ["/mensagens", "Chat"], ["/config", "Configurações"]];
  const achou = mapa.find(([prefixo]) => p === prefixo || p.startsWith(prefixo + "/"));
  return achou ? achou[1] : null;
}

export default function GavetaLateral() {
  const { aberta, aba, setAba, fechar, contexto, pedido, limparContexto, consumirPedido } = useGaveta();
  const { pathname } = useLocation();
  const tela = nomeDaTela(pathname);
  const { unread, convites } = useMensagens();
  const total = unread + convites;

  useEffect(() => {
    if (!aberta) return undefined;
    const onKey = (e) => { if (e.key === "Escape") fechar(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberta, fechar]);

  if (!aberta) return null;
  const tabCls = (ativo) => `flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-md text-sm font-semibold transition-colors ${ativo ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`;
  return (
    <aside data-testid="gaveta" role="complementary" aria-label="Assistente IA e Conversas"
      className="fixed z-50 inset-0 md:inset-y-0 md:left-auto md:right-0 md:w-[420px] bg-white md:border-l md:border-stone-200 md:shadow-[-12px_0_32px_rgba(28,25,23,0.10)] flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
      <div className="flex items-center gap-2 px-3 pt-3 pb-2">
        <div role="tablist" className="flex-1 flex gap-1 rounded-lg bg-stone-100 p-1">
          <button type="button" role="tab" aria-selected={aba === "ia"} data-testid="gaveta-aba-ia" onClick={() => setAba("ia")} className={tabCls(aba === "ia")}>
            <Sparkles className="w-4 h-4" strokeWidth={1.75} /> Assistente IA
          </button>
          <button type="button" role="tab" aria-selected={aba === "equipe"} data-testid="gaveta-aba-equipe" onClick={() => setAba("equipe")} className={tabCls(aba === "equipe")}>
            <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> Conversas
            {total > 0 && <span data-testid="gaveta-equipe-contador" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#c8502d] text-white text-[11px] font-bold inline-flex items-center justify-center">{total}</span>}
          </button>
        </div>
        <button type="button" onClick={fechar} data-testid="gaveta-fechar" aria-label="Fechar painel" className="h-9 w-9 rounded-full inline-flex items-center justify-center text-stone-500 hover:bg-stone-100">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className={`${aba === "ia" ? "flex" : "hidden"} flex-col flex-1 min-h-0 overflow-y-auto px-4 pb-4`} data-testid="gaveta-painel-ia">
        <h2 className="font-display text-xl font-semibold text-stone-900 mt-1">Assistente IA</h2>
        <p className="text-sm text-stone-500 mb-3">Consulte sua agenda e os registros disponíveis.</p>
        <div className="flex items-center gap-2 mb-3 text-xs text-stone-500" data-testid="gaveta-contexto">
          Contexto:
          {contexto ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-stone-900 text-white pl-2.5 pr-1 py-0.5 font-semibold min-w-0" data-testid="gaveta-contexto-processo">
              <span className="truncate">{contexto.tipo === "audiencia" ? "Esta audiência" : "Este processo"} · {contexto.rotulo}</span>
              <button type="button" onClick={limparContexto} data-testid="gaveta-contexto-limpar" aria-label="Voltar para Minha agenda"
                className="w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-white/20"><X className="w-3 h-3" /></button>
            </span>
          ) : <span className="font-semibold text-stone-700">Minha agenda{tela ? <span className="font-normal text-stone-500" data-testid="gaveta-tela"> · você está em {tela}</span> : null}</span>}
        </div>
        <PerguntaAgenda key={chaveContexto(contexto)} exemplos={contexto ? (contexto.tipo === "audiencia" ? EXEMPLOS_AUDIENCIA : EXEMPLOS_PROCESSO) : EXEMPLOS_ASSISTENTE}
          contexto={contexto} tela={tela} pedido={pedido} aoConsumirPedido={consumirPedido} gaveta aoNavegar={() => { if (window.matchMedia && !window.matchMedia("(min-width: 768px)").matches) fechar(); }} />
        <p className="mt-auto pt-4 text-[11px] text-stone-400" data-testid="gaveta-rodape-ia">Respostas baseadas nos registros acessíveis a você. Somente consulta: o assistente não altera nem envia nada. Nomes e números são trocados por códigos antes de irem à IA; processos em segredo de justiça não são enviados.</p>
      </div>
      <div className={`${aba === "equipe" ? "flex" : "hidden"} flex-col flex-1 min-h-0`} data-testid="gaveta-painel-equipe">
        <ChatRapido ativo={aberta && aba === "equipe"} aoNavegar={fechar} />
      </div>
    </aside>
  );
}
